/**
 * Pure reducer + types + localStorage helpers for the onboarding
 * state machine. Isolated from React so it's trivially testable
 * without jsdom or a React test harness.
 *
 * Schema-versioned persistence:
 *   - Namespace: "wai:onboarding:v2:preview"
 *   - Payload: { version: 1, step, answers }
 *
 * On load, mismatched-version or unparseable blobs are discarded
 * silently and initial state is used. At Phase 7 cutover, a
 * migration step reads version N, transforms to version N+1, and
 * writes to Supabase via handleJourneySelection() +
 * saveAthleteContext().
 *
 * Private-mode Safari throws on localStorage.setItem() — wrapped in
 * try/catch; persistence degrades to in-memory only, state still
 * works for the session.
 */

import type {
  HealthFreqId,
  JourneyId,
  QualPaceId,
  SocialGroupId,
  TimeGoalType,
} from '../fixtures/onboarding';
import type { DistanceUnit } from '../lib/firstPlan/trainingData';

// ──────────────────────────────────────────────────────────────────────────
// Types
// ──────────────────────────────────────────────────────────────────────────

export type BodyUnit = 'imperial' | 'metric';

/** Flat bag of answers — keys aligned with the state schema documented
 *  in the Phase 2 README. All optional — a fresh user has none. */
export type OnboardingAnswers = {
  // Step 1
  journey?: JourneyId;

  /**
   * ⚖️ O2 / CD onboarding-chapters handoff (founder-ratified 2026-08-30),
   * "New screen 1 — Your name" — the display name the coaches address the
   * athlete by. Collected by `steps/NameStep.tsx`, persisted by the route.
   *
   * ⛔ THIS IS NOT THE SIGN-IN IDENTITY. It is the "what should we call you"
   * answer and nothing else — the same question `context_data.preferred_name`
   * has always answered (CW-6), which is why `lib/displayName`'s chain reads
   * that key FIRST. The provider's own name is never touched.
   *
   * `''` is MEANINGFUL: it is a recorded skip. The step writes it on both the
   * empty-submit and the "Skip for now" exits so the choice survives
   * Back-navigation (an `undefined` would re-prefill from the sign-in name and
   * silently undo her). ⛔ The persist layer must treat `''` as "write no
   * name" — the Profile card stays empty, exactly as it does today.
   */
  displayName?: string;

  /**
   * B454 (walk 2026-10-22) — the name screen has handed off. It lives in the
   * answer bag, and so in the persisted blob, because the name screen and the
   * journey question share wizard step 1: a reload on the journey question used
   * to land on the name screen again (the flag was route state and reset), and
   * with the name blank when the athlete had skipped it. Back from the journey
   * question writes `false`, which re-opens the name screen with what she typed.
   *
   * ⛔ A navigation flag, not an answer: nothing projects it onto the
   * completion payload (`buildOnboardingPayload` reads named keys only).
   */
  nameStepDone?: boolean;

  // Step 2
  location?: string;
  locationConfirmed?: boolean;
  manualCity?: string;
  manualCountry?: string;
  /**
   * B48 (2026-08-29, founder field report) — CITY-LEVEL coordinates for
   * `location`, when the geocoder gave us any.
   *
   * Before this the wizard carried a city STRING and nothing else, so a
   * completed onboarding wrote `_city` with `_latitude`/`_longitude` left null
   * — and weather, the vitamin-D band and every other coordinate consumer
   * silently degraded for the whole life of the account (founder's own row:
   * city "Los Altos, California", null coords). `Step2Location` already had
   * the numbers in hand — `geocodeCityOnSave` returns `{label, lat, lon}` —
   * and dropped them on the floor at Continue.
   *
   * ⚖️ §8.2 — the pair stored here is ALREADY rounded to city level (2 dp ≈
   * 1.1 km) by `roundToCityLevel` at the moment it is captured. A precise fix
   * never enters this bag, because a precise fix never enters this step: the
   * coordinates come from a forward geocode of a typed city name, never from
   * the device.
   *
   * `for` is the exact `location` string these coordinates describe, and it is
   * LOAD-BEARING. The resolve that produces them can land after the athlete
   * has gone Back and typed a different city, so the consumer
   * (`buildOnboardingPayload`) projects the pair ONLY when `for` still equals
   * the `location` being saved. A stale pair is dropped, never written against
   * the wrong city — the fail-safe direction is "no coordinates", never
   * "someone else's coordinates".
   */
  locationCoords?: { lat: number; lon: number; for: string };

  // Step 3
  parq?: Record<string, boolean>;
  // B2 (COPPA, founder-ratified 2026-07-04) — REQUIRED birth month+year:
  // month+year (never full DOB) keeps sensitivity low while making the
  // derived age exact; a bare year is ±1 ambiguous, which matters at the
  // under-13 boundary. Self-attested (the CG-3 caveat stands).
  birthYear?: number;
  birthMonth?: number;

  // Step 4
  bodyUnit?: BodyUnit;
  weightLbs?: string;
  weightKg?: string;
  heightFt?: string;
  heightIn?: string;
  heightCm?: string;

  // Step 5 — VDOT branch (competitive_solo / competitive_team / time_goal)
  raceDistance?: string;
  raceTimeSeconds?: number;
  skipPace?: boolean;

  // Step 5 — qualitative branch (health_fitness / social)
  qualPace?: QualPaceId;

  /**
   * ⚖️ O2 / CD onboarding-chapters handoff, "Distance units — one
   * profile-level answer" — THE unit field. ONE answer, written by the
   * weekly-mileage question in the training-data beat (its mi/km toggle) and
   * saved with the completion as `distance_unit` (B457), where
   * `resolveDistanceUnit` (`lib/vdot.ts`) reads it back for the plan and Today.
   *
   * Ruling 3 (2026-10-22) removed the universal pace screen that used to share
   * this toggle ("Do you know your usual pace?", and its `paceSecondsPerUnit`
   * answer, which was collected and never used).
   *
   * ⛔ Deliberately ONE key, not one per screen. Two keys is how a unit switch
   * on one screen silently disagrees with the other, and the handoff's whole
   * point is that the mileage answer persists as a BAND ID rather than a
   * localized string precisely so switching the unit never loses a selection.
   *
   * ⚠️ NO DEFAULT. `undefined` means "the athlete has not said", which is not
   * the same as miles. The weekly question PRESELECTS `'km'` only from a known
   * location country (`lib/onboardingUnits.preselectUnit`) and otherwise shows
   * miles until a tap; the preselect is never written on its own.
   *
   * Shares `DistanceUnit` with `lib/firstPlan/trainingData` so the "same unit
   * field" claim is enforced by the type checker rather than by a comment.
   */
  distanceUnit?: DistanceUnit;

  // Step 6 — competitive
  discipline?: string;
  goalEvent?: string | null;

  // Step 6 — time_goal
  timeGoalType?: TimeGoalType;
  timeGoalDist?: string | null;

  // Step 6 — health_fitness
  healthFreq?: HealthFreqId;
  healthMotives?: string[];

  // Step 6 — social
  socialGroup?: SocialGroupId;
  socialFreq?: string | null;

  // F-520 — the SP-3 data-gate answers, now collected IN the wizard (the
  // in-wizard data beat, after the last question step and before the guardian
  // beat / Welcome — see routes/onboarding.tsx). This is the canonical patch
  // produced by `buildTrainingDataPatch` (weekly_mileage + band/exact +
  // experience + injuries) — the SAME keys the first-look collection beat
  // writes. `buildOnboardingPayload` range-guards + folds it into the atomic
  // completion POST, so a fresh wizard completion lands these in athlete_context
  // and first-look's `missingTrainingData(ctx)` is empty (mounts straight to
  // generating). Kept as an opaque bag because the values are already canonical.
  trainingData?: Record<string, unknown>;
};

/** Current step in the 7-step flow. Step 7 is the outcome/welcome screen. */
export type StepNumber = 1 | 2 | 3 | 4 | 5 | 6 | 7;

export type OnboardingState = {
  step: StepNumber;
  answers: OnboardingAnswers;
};

export type OnboardingAction =
  | { type: 'SET_STEP'; step: StepNumber }
  | { type: 'NEXT_STEP' }
  | { type: 'PREV_STEP' }
  | { type: 'SET_ANSWER'; patch: Partial<OnboardingAnswers> }
  // SP-3 (folded filler) — replace the whole wizard state with a per-user
  // persisted blob once the signed-in user id is known (see
  // useOnboardingState). Distinct from SET_STEP+SET_ANSWER so a resume is one
  // atomic dispatch and can't interleave with an in-flight answer edit.
  | { type: 'HYDRATE'; state: OnboardingState }
  | { type: 'RESET' };

// ──────────────────────────────────────────────────────────────────────────
// Reducer
// ──────────────────────────────────────────────────────────────────────────

export const INITIAL_STATE: OnboardingState = {
  step: 1,
  answers: {},
};

export function onboardingReducer(
  state: OnboardingState,
  action: OnboardingAction,
): OnboardingState {
  switch (action.type) {
    case 'SET_STEP':
      return { ...state, step: clampStep(action.step) };

    case 'NEXT_STEP':
      return { ...state, step: clampStep((state.step + 1) as StepNumber) };

    case 'PREV_STEP':
      return { ...state, step: clampStep((state.step - 1) as StepNumber) };

    case 'SET_ANSWER':
      return { ...state, answers: { ...state.answers, ...action.patch } };

    case 'HYDRATE':
      return { step: clampStep(action.state.step), answers: { ...action.state.answers } };

    case 'RESET':
      return INITIAL_STATE;

    default: {
      // Exhaustiveness check — TS errors if a new action type isn't handled.
      action satisfies never;
      return state;
    }
  }
}

function clampStep(n: number): StepNumber {
  if (n < 1) return 1;
  if (n > 7) return 7;
  return n as StepNumber;
}

// ──────────────────────────────────────────────────────────────────────────
// localStorage persistence
// ──────────────────────────────────────────────────────────────────────────

export const STORAGE_KEY = 'wai:onboarding:v2:preview';
export const STORAGE_VERSION = 1;

type PersistedBlob = {
  version: typeof STORAGE_VERSION;
  step: StepNumber;
  answers: OnboardingAnswers;
  /** The auth user id that owns this local wizard state — the blob's CLAIM.
   *  Stamped on persist so the Today localStorage→server migration can reject
   *  a blob left behind by a DIFFERENT (e.g. deleted) account in the same
   *  browser.
   *
   *  B26 — absent means UNCLAIMED, not "foreign": no account has ever taken
   *  ownership of these answers. Two ways that happens: a legacy blob written
   *  before this field existed, and (the B26 bug, 2026-08-26) a wizard walked
   *  with NO session at all — the first organic external user answered every
   *  question signed out, and his answers are sitting in his browser under
   *  exactly this shape. An unclaimed blob is claimable by the FIRST account
   *  to sign in at this browser; see `loadPersistedStateForUser` /
   *  `purgeForeignPersistedState`. */
  userId?: string;
};

/** Read + validate the raw persisted blob (including the owner `userId`).
 *  Returns null if no blob, bad JSON, version mismatch, out-of-range
 *  step, or if localStorage is unavailable (SSR / private mode Safari). */
function readPersistedBlob(): PersistedBlob | null {
  if (typeof window === 'undefined') return null;
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<PersistedBlob>;
    if (parsed?.version !== STORAGE_VERSION) return null;
    if (typeof parsed.step !== 'number' || parsed.step < 1 || parsed.step > 7) {
      return null;
    }
    if (typeof parsed.answers !== 'object' || parsed.answers === null) {
      return null;
    }
    return {
      version: STORAGE_VERSION,
      step: parsed.step as StepNumber,
      answers: parsed.answers as OnboardingAnswers,
      userId: typeof parsed.userId === 'string' ? parsed.userId : undefined,
    };
  } catch {
    return null;
  }
}

/** Read + parse persisted state. Returns null if no blob, bad JSON,
 *  version mismatch, or if localStorage is unavailable (SSR / private
 *  mode Safari). Ownership-agnostic — for wizard hydration only. */
export function loadPersistedState(): OnboardingState | null {
  const blob = readPersistedBlob();
  return blob ? { step: blob.step, answers: blob.answers } : null;
}

/**
 * Like {@link loadPersistedState}, but ownership-aware. Returns the persisted
 * state only when the blob is STAMPED with `userId`. A blob stamped with a
 * DIFFERENT user id, an UNSTAMPED blob, or a missing `userId` argument, are
 * all treated as ABSENT (returns null).
 *
 * This gates the Today localStorage→server onboarding-completion
 * migration. The guarantee it must keep, and does: **one account's answers can
 * never reach another account.** Account A's blob is stamped with A's id the
 * moment A signs in, so B can never adopt it. See fillers.md "Stale
 * localStorage re-onboards a fresh signup".
 *
 * B110 (2026-09-21) — an UNSTAMPED blob used to be treated as UNCLAIMED
 * (B26, 2026-08-26) and adopted by whichever account signed in next, because
 * rejecting one stranded the first organic external user who had walked the
 * entire wizard signed out. That rescue is now retired: an unstamped blob is
 * ordinary foreign state — a stranger's answers, possibly a minor's, that
 * must never seed a new account — and the read side treats it exactly like a
 * different user's stamped blob. See `purgeForeignPersistedState` below for
 * the write-side half of this same rule.
 */
export function loadPersistedStateForUser(
  userId: string | null | undefined,
): OnboardingState | null {
  if (!userId) return null;
  const blob = readPersistedBlob();
  if (!blob) return null;
  // B110 — stamped with someone ELSE's id, or UNSTAMPED, is foreign: absent.
  // Only a blob stamped with THIS id restores.
  if (blob.userId !== userId) return null;
  return { step: blob.step, answers: blob.answers };
}

/** Best-effort persist. Swallows errors (private-mode Safari, quota
 *  exceeded, etc.) — the flow still works, just without persistence.
 *  Stamps the owning `userId` when known (see PersistedBlob.userId). */
export function persistState(state: OnboardingState, userId?: string | null): void {
  if (typeof window === 'undefined') return;
  const blob: PersistedBlob = {
    version: STORAGE_VERSION,
    step: state.step,
    answers: state.answers,
    ...(userId ? { userId } : {}),
  };
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(blob));
  } catch {
    // Intentional silent failure — see file header.
  }
}

/**
 * F-113 — reconcile the persisted wizard blob with the signed-in user. Called
 * on auth-state change, so a prior account's onboarding answers — which can
 * include a MINOR's PAR-Q and body metrics — don't linger readable in a shared
 * browser after a different user signs in.
 *
 * Two cases, one rule (ownership):
 *   - stamped with THIS id           → kept (an in-progress wizard survives a
 *                                      token refresh).
 *   - anything else (a DIFFERENT id,
 *     or UNSTAMPED)                  → FOREIGN, purged (the F-113 guarantee).
 *
 * B110 (2026-09-21) — an UNSTAMPED blob used to be CLAIMED here instead of
 * purged (B26, 2026-08-26): re-stamped in place for whoever was signing in,
 * on the theory that the only person who could be sitting at that browser
 * was the person who had typed the answers. That claim is retired — a
 * stranger's answers must never seed a new account, unstamped or not — so an
 * unstamped blob now takes the same purge this function already gave a
 * differently-stamped one. See `loadPersistedStateForUser` above for the
 * read-side half of this same rule.
 *
 * No-op without a user id (owner unknown — never purge on a null settle).
 */
export function purgeForeignPersistedState(userId: string | null | undefined): void {
  if (typeof window === 'undefined' || !userId) return;
  const blob = readPersistedBlob();
  if (!blob) return;
  if (blob.userId === userId) return;
  clearPersistedState();
}

/** Clear persisted state — used by the `?reset` URL param and the
 *  future Phase 7 cutover once state is migrated to Supabase. */
export function clearPersistedState(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

// ──────────────────────────────────────────────────────────────────────────
// Phase 7 PR 1 — canonical "is this onboarding complete?" detector
// ──────────────────────────────────────────────────────────────────────────

/**
 * Returns true iff the persisted onboarding state represents a completed
 * flow. Centralized here so the Today gate's localStorage-migration path
 * (`src/app/src/routes/today.tsx`) and the test fixtures agree on the
 * canonical signal — tests that hardcode `state.step === 7` would drift
 * if the canonical shape evolves; tests that call `isOnboardingComplete()`
 * stay aligned.
 *
 * Current canonical signal: **`state.step === 7 && Boolean(state.answers.journey)`**
 *
 * Rationale:
 *   - `step === 7` is the Welcome/outcome screen — the reducer only
 *     reaches it via NEXT_STEP from Step 6, which means Steps 1-6 ran
 *     through to their natural end.
 *   - `answers.journey` set is the minimum data check — Step 1 is
 *     "pick your journey" and the user CANNOT progress past Step 1
 *     without selecting one. Belt-and-suspenders against malformed
 *     localStorage blobs that have a high `step` but blank answers.
 *
 * If the canonical signal evolves (e.g., a `completed: true` flag is
 * added to the persisted blob), update this function and the migration
 * path + tests follow automatically.
 */
export function isOnboardingComplete(state: OnboardingState): boolean {
  return state.step === 7 && Boolean(state.answers.journey);
}
